import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useIsVisible from "../../../../../hooks/useIsVisible";
import CompareSelected from "../../../../../parts/CompareSelected";
import { MobileMonthSelector } from "../../../../../parts/MobileMonthSelector";
import SvgIcon from "../../../../../parts/SvgIcon";
import { ReportsBackHandler } from "../../components/ReportsBackHandler";
import CompareListPhotosModal from "./CompareListPhotosModal";
import CompareToTimelineModal from "./CompareToTimelineModal";

import RangeSelector from "./RangeSelector";
import TimelinePhotos from "./TimelinePhotos";

const rangeConfig = {
  twoWeeks: {
    key: "twoWeeks",
    labelKey: "reports.rangeSelector.twoWeeks",
    amount: 2,
    period: "week",
  },
  oneMonth: {
    key: "oneMonth",
    labelKey: "reports.rangeSelector.oneMonth",
    amount: 1,
    period: "month",
  },
  threeMonths: {
    key: "threeMonths",
    labelKey: "reports.rangeSelector.threeMonths",
    amount: 3,
    period: "month",
  },
  sixMonths: {
    key: "sixMonths",
    labelKey: "reports.rangeSelector.sixMonths",
    amount: 6,
    period: "month",
  },
  all: {
    key: "all",
    labelKey: "reports.rangeSelector.lifetime",
  },
};

const TimeLineView = ({ locals, comesFromBodyComp }) => {
  const { t } = useTranslation("athlete-portal");
  const ranges = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(rangeConfig).map(([k, v]) => [
          k,
          { ...v, label: v.labelKey ? t(v.labelKey) : v.key },
        ])
      ),
    [t]
  );
  const { dailyStats, query } = locals;
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const { name, _id: athleteId } = locals.athlete || locals.activeUser;
  const isCoach = !!locals.athlete;
  const today = moment();
  const listviewUrl = isCoach
    ? `/athlete-portal/metrics/progress-photos?date=${moment(today).format(
        "YYYYMMDD"
      )}&athlete=${athleteId}&view=List`
    : `/dashboard/metrics/progress-photos?date=${moment(today).format(
        "YYYYMMDD"
      )}&view=List`;
  const [selectedRange, setSelectedRange] = useState(() => ranges.threeMonths);
  const [listOfPhotos, setListOfPhotos] = useState({
    front: [],
    back: [],
    side: [],
  });
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isSelectinOnlyOne, setIsSelectingOnlyOne] = useState(false);

  const [isModalOpened, setIsModalOpened] = useState(false);

  const [expandedList, setExpandedList] = useState(null);

  const [endDate, setEndDate] = useState(today);
  const ref = useRef();
  const isVisible = useIsVisible(ref);
  const getFilteredStats = (type, range) => {
    if (range.key === rangeConfig.all.key) {
      return dailyStats.filter((stat) => stat.bodyPhotos?.[type]);
    }
    const startOfRange = moment(endDate)
      .subtract(range.amount, range.period)
      .format("YYYYMMDD");
    const endOfRange = moment(endDate).format("YYYYMMDD");

    const filtered = dailyStats.filter(
      (stat) =>
        stat.bodyPhotos?.[type] &&
        Object.keys(stat.bodyPhotos).length &&
        stat.utcDate >= startOfRange &&
        stat.utcDate <= endOfRange
    );
    return filtered;
  };

  const selectPhotos = (stat, key) => {
    if (selectedPhotos.length <= 2) {
      // Add or remove photo based on its selection state
      const selectedPic = { stat, key };
      setSelectedPhotos((prevSelectedPhotos) => {
        if (
          prevSelectedPhotos.some(
            (item) => item.stat === stat && item.key === key
          )
        ) {
          return prevSelectedPhotos.filter(
            (selectedPhoto) =>
              selectedPhoto.stat !== stat || selectedPhoto.key !== key
          );
        }
        return selectedPhotos.length < 2
          ? [...prevSelectedPhotos, selectedPic]
          : prevSelectedPhotos;
      });
    }
  };

  const handleReset = () => {
    setIsModalOpened(false);
    setSelectedPhotos([]);
    setIsSelecting(false);
    setIsSelectingOnlyOne(false);
  };

  useEffect(() => {
    setListOfPhotos({
      front: getFilteredStats("front", selectedRange),
      back: getFilteredStats("back", selectedRange),
      side: getFilteredStats("side", selectedRange),
    });
  }, [selectedRange, athleteId, endDate]);

  // Resync selectedRange when ranges get translated (language switch)
  useEffect(() => {
    setSelectedRange((prev) => ranges[prev.key] ?? prev);
  }, [ranges]);

  // Selection order follows whatever the athlete tapped first, so sort the
  // { stat, key } pairs as a unit — the compare modal reads the stats and the
  // angles positionally and they have to stay lined up.
  const sortedSelection = [...selectedPhotos].sort(
    (a, b) => a.stat.utcDate - b.stat.utcDate
  );

  return (
    <article role="report-content" id="progress-photos-tl">
      <header role="timeline-header">
        <hgroup>
          <div className="top-header only-show-mobile">
            <ReportsBackHandler
              title={t("progressPhotos.title")}
              locals={locals}
            />
            <MobileMonthSelector date={endDate} setDate={setEndDate} />
          </div>
          <div className="flex gap-2 items-center">
            {comesFromBodyComp && (
              <SvgIcon
                classNames="cursor-pointer "
                onClickFn={() => {
                  history.back();
                }}
                src={"/img/new-icons/back-button.svg"}
              />
            )}
            <h3 className="mobile-hidden">{t("progressPhotos.title")}</h3>
          </div>
          <div ref={ref} role="buttons-container">
            {isSelecting || isSelectinOnlyOne ? (
              <>
                <button data-button-type="secondary" onClick={handleReset}>
                  {t("calendar.cancel")}
                </button>
                {!isSelectinOnlyOne && (
                  <button
                    disabled={selectedPhotos.length < 2}
                    data-button-type="primary"
                    onClick={
                      selectedPhotos.length === 2
                        ? () => setIsModalOpened(true)
                        : null
                    }
                  >
                    {t("progressPhotos.compare")}
                  </button>
                )}
              </>
            ) : (
              <>
                <a
                  role="button"
                  data-button-type="secondary"
                  href={`${listviewUrl}${organizationCoachParam}`}
                  // className="dark-button"
                >
                  {t("progressPhotos.listView")}
                </a>
                <button
                  role="button"
                  data-button-type="outline"
                  onClick={() => {
                    setIsSelecting(true);
                    setIsSelectingOnlyOne(true);
                  }}
                >
                  <span>{t("progressPhotos.comparePhotoToTimeline")}</span>
                </button>

                <button
                  // className="dark-button green"
                  data-button-type="primary"
                  onClick={() => setIsSelecting(true)}
                >
                  {t("progressPhotos.compareTwoPhotos")}
                </button>
              </>
            )}
          </div>
        </hgroup>
        <RangeSelector
          selectedRange={selectedRange}
          setRange={setSelectedRange}
          ranges={ranges}
        />
      </header>
      <div id="timeline-container">
        {["front", "back", "side"].map((type, i) => {
          return (
            <TimelinePhotos
              listOfPhotos={listOfPhotos[type]}
              type={type}
              selectPhotos={selectPhotos}
              choosing={isSelecting}
              choosingOne={isSelectinOnlyOne}
              selectedPhotos={selectedPhotos}
              key={`${type}${i}`}
              expandedList={expandedList}
              setExpandedList={setExpandedList}
            />
          );
        })}
      </div>
      <CompareSelected
        label1={t("progressPhotos.image1")}
        label2={t("progressPhotos.image2")}
        selectedItem1={
          selectedPhotos[0]
            ? moment(selectedPhotos[0].stat.date, "YYYYMMDD").format(
                "MMM D,YYYY"
              )
            : null
        }
        selectedItem2={
          selectedPhotos[1]
            ? moment(selectedPhotos[1].stat.date, "YYYYMMDD").format(
                "MMM D,YYYY"
              )
            : null
        }
        compareFunction={() => setIsModalOpened(true)}
        closeFunction={() => setSelectedPhotos([])}
        isTopButtonVisible={isVisible}
        compareName={"Photos"}
        isModalOpened={
          isModalOpened || (isSelectinOnlyOne && selectedPhotos.length === 1)
        }
      />
      {isModalOpened && (
        <CompareListPhotosModal
          selectedPhotos={sortedSelection.map((item) => item.stat)}
          setSelectedPhotos={setSelectedPhotos}
          closeModalAndReset={handleReset}
          closeModal={() => setIsModalOpened(false)}
          sides={sortedSelection.map((item) => item.key)}
          compareOnlyTwo={true}
        />
      )}
      {isSelectinOnlyOne && selectedPhotos.length === 1 && (
        <CompareToTimelineModal
          selectedPhoto={selectedPhotos[0]}
          setSelectedPhotos={setSelectedPhotos}
          listToCompare={listOfPhotos[selectedPhotos[0].key]}
          closeModalAndReset={handleReset}
          closeModal={() => setIsModalOpened(false)}
        />
      )}
    </article>
  );
};

export default TimeLineView;
